import { t } from '@lingui/macro';
import type { AxiosResponse } from 'axios';
import lodashGet from 'lodash-es/get';
import moment from 'moment';

import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { I18N_FORMAT_MAP } from '@rover/kibble/styles';
import petFactory from '@rover/react-lib/src/factories/petFactory';
import camelGet from '@rover/react-lib/src/utils/camelGet';
import { getAxiosInstance } from '@rover/rsdk/src/modules/Network/axios';
import type { Snakeify } from '@rover/rsdk/src/modules/Network/object-utilities';
import { camelize, snakeify } from '@rover/rsdk/src/modules/Network/object-utilities';
import type {
  FetchPetImageResponse,
  Image,
  Pet,
  PetDetailsOptions,
  PetsResponse,
} from '@rover/types';
import { fullApiUrl } from '@rover/utilities/apiUrl';

import DefaultAPIErrorHandler from './DefaultAPIErrorHandler';
import { get as personSummaryGet } from './personSummary.api';

export const PET_LIST_URL = fullApiUrl('pets/');
export const MY_PETS_URL = 'my-pets/';

export const scrubAPetData = (rawPet: Partial<Pet>): Pet => {
  return petFactory({ ...rawPet });
};

export const scrubRawData = (rawPetsData: Array<Partial<Pet> & Pick<Pet, 'birthday'>>): Pet[] =>
  rawPetsData.map(scrubAPetData);

export const get = async (): Promise<PetsResponse> => {
  try {
    const data = await camelGet<PetsResponse>({ url: MY_PETS_URL });
    return {
      pets: scrubRawData(data.pets),
      memorializedPets: scrubRawData(data.memorializedPets),
    };
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error while fetching pets.`);
  }
};
export const getMemberPets = async (
  memberUrl: string
): Promise<{
  pets: Pet[];
  memorializedPets: Pet[];
  memberName: string;
}> => {
  try {
    const data = await personSummaryGet(memberUrl);
    return {
      pets: scrubRawData(data.pets),
      memorializedPets: scrubRawData(data.memorializedPets),
      memberName: data.shortName,
    };
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error while fetching pets.`);
  }
};
export const getDetail = async (petUrl: string): Promise<Pet> => {
  const ai = getAxiosInstance();

  try {
    const { data } = await ai.get<Snakeify<Pet>>(petUrl);
    return scrubAPetData(camelize(data, true));
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details.`);
  }
};
export const getPetDetailsOptions = async (): Promise<PetDetailsOptions> => {
  const ai = getAxiosInstance();
  const petDetailsOptionsUrl = fullApiUrl('pets/');

  try {
    const response = await ai.request({
      url: petDetailsOptionsUrl,
      method: 'options',
    });
    const petDetailsOptions = lodashGet(response, 'data.actions.POST.pet_details.child.children');
    return camelize(petDetailsOptions, true);
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details options.`);
  }
};
export const deletePet = async (petUrl: string): Promise<boolean> => {
  const ai = getAxiosInstance();

  try {
    await ai.delete(petUrl);
    return true;
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet.`);
  }
};
export const memorializePet = async (
  petUrl: string,
  memorialDate?: Date | string | null
): Promise<boolean> => {
  const ai = getAxiosInstance();

  try {
    await ai.put(petUrl, {
      is_memorialized: true,
      memorial_date: memorialDate,
    });
    return true;
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error memorializing pet.`);
  }
};
export const getImages = async (url: string, pageSize?: number): Promise<FetchPetImageResponse> => {
  const ai = getAxiosInstance();

  try {
    const { data } = await ai.get<Snakeify<FetchPetImageResponse>>(url, {
      params: {
        page_size: pageSize,
      },
    });
    return camelize(data, true);
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet images.`);
  }
};
export const deleteImage = async (imageUrl: string): Promise<boolean> => {
  const ai = getAxiosInstance();

  try {
    await ai.delete(imageUrl);
    return true;
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet image.`);
  }
};
export const togglePublicPrivateImage = async (
  togglePrivacyUrl: string,
  imageId: string
): Promise<boolean> => {
  const ai = getAxiosInstance();

  try {
    await ai.post(togglePrivacyUrl, {
      image_id: imageId,
    });
    return true;
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error setting public/private image.`);
  }
};
export const addImage = async (
  petImagesUrl: string,
  imageUrl: string,
  caption?: string
): Promise<Image> => {
  const ai = getAxiosInstance();

  try {
    const response = await ai.post<Snakeify<Image>>(petImagesUrl, {
      image_url: imageUrl,
      caption,
    });
    return camelize(response.data, true);
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error adding a pet image.`);
  }
};
export const setImageToFirst = async (orderingUrl: string): Promise<void> => {
  const ai = getAxiosInstance();

  try {
    ai.put(orderingUrl, {
      previous: null,
    });
  } catch (e: any) {
    throw DefaultAPIErrorHandler(e, t`There was an error updating the pet image.`);
  }
};

export const postOrPut = async (pet: Partial<Pet>): Promise<Pet> => {
  const ai = getAxiosInstance();
  const birthday =
    pet.birthday ||
    moment()
      .subtract(pet.ageYears, 'years')
      .subtract(pet.ageMonths, 'months')
      .format(I18N_FORMAT_MAP[DEFAULT_LANG].DATE_ISO);
  let rawData: Partial<Pet> = { ...pet, birthday };
  rawData = Object.keys(rawData)
    .filter((k) => rawData[k] !== null)
    .reduce((obj, k) => {
      obj[k] = rawData[k]; // eslint-disable-line no-param-reassign

      return obj;
    }, {});
  try {
    const snakeData = snakeify(rawData, true);
    const { data } = await ai<Snakeify<Pet>>({
      method: pet.url ? 'PUT' : 'POST',
      url: pet.url ? pet.url : PET_LIST_URL,
      data: { ...snakeData },
    });
    return scrubAPetData(camelize(data, true));
  } catch (e: any) {
    return DefaultAPIErrorHandler(e, t`There was an error saving your pet information.`);
  }
};
export const postPetMetricNewSSU = async (pet: Partial<Pet>, metricUrl: string): Promise<void> => {
  const ai = getAxiosInstance();
  const data = snakeify(pet);
  await ai
    .post(metricUrl, data)
    .then(() => {})
    .catch(() => {});
};
